/// <reference types="google.maps" />
// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../types/react-places-autocomplete.d.ts" />
import React from "react";
import { Error, useDropdownLayer } from "@narmi/design_system";
import PlacesAutocomplete, {
  PlacesAutocompleteRenderProps,
  Suggestion,
} from "react-places-autocomplete";
import cc from "classcat";

const SEARCH_FIELD_ID = "searchPlaces";

interface AddressInputAndMenuProps {
  streetAddressLabel?: string;
  suggestions: readonly Suggestion[];
  error?: string;
  getInputProps: PlacesAutocompleteRenderProps["getInputProps"];
  getSuggestionItemProps: PlacesAutocompleteRenderProps["getSuggestionItemProps"];
  loading: boolean;
  onChange?: (value: string) => void;
  autoComplete?: string;
}

const AddressInputAndMenu = ({
  streetAddressLabel,
  suggestions,
  error,
  getInputProps,
  getSuggestionItemProps,
  loading,
  onChange,
  autoComplete,
}: AddressInputAndMenuProps) => {
  const isOpen = suggestions.length > 0;

  const { anchorProps, layerProps } = useDropdownLayer({
    isOpen,
    setIsOpen: () => {},
    ariaPopupType: "listbox",
    placement: "bottom",
    alignment: "start",
    matchWidth: true,
  });

  return (
    <div>
      <div
        className={cc([
          "nds-combobox-address",
          {
            "nds-combobox-address--active": isOpen,
          },
        ])}
      >
        <div className={cc(["nds-input-address", { error }])}>
          <div
            className="nds-input-box-address"
            {...(anchorProps as React.HTMLAttributes<HTMLDivElement> & {
              ref: React.Ref<HTMLDivElement>;
            })}
          >
            <div className="nds-input-column-address">
              <input
                id={SEARCH_FIELD_ID}
                {...getInputProps({
                  placeholder: streetAddressLabel,
                })}
                aria-label={streetAddressLabel}
                key="nds-text"
                type="text"
                required
                autoComplete={autoComplete}
                onBlur={(event) => {
                  // if the user hits tab or clicks outside the box while the suggestion box is open
                  // onSelect does't get called by PlacesAutocomplete
                  // and the box closes but the whole address string remains as the value of the input.
                  // To avoid that, set the value to up until the first comma.
                  if (isOpen) {
                    const { value } = event.target;
                    if (value?.includes(",")) {
                      onChange?.(value.substring(0, value.indexOf(",")));
                    } else {
                      onChange?.(value);
                    }
                  }
                  getInputProps().onBlur();
                }}
                // @ts-expect-error -- error is not a DOM input attribute; preserved
                // as-is from the JS version (React passes it through to the element)
                // eslint-disable-next-line react/no-unknown-property
                error={error}
              />
              <label htmlFor={SEARCH_FIELD_ID}>{streetAddressLabel}</label>
            </div>
          </div>
          {isOpen && (
            <div
              className={cc([
                "nds-combobox-list-address",
                "list--reset bgColor--white",
                {
                  "nds-combobox-list-address--active": isOpen,
                },
              ])}
              {...(layerProps as React.HTMLAttributes<HTMLDivElement> & {
                ref: React.Ref<HTMLDivElement>;
              })}
              style={{
                ...layerProps.style,
                zIndex: 4,
              }}
            >
              {loading && <div>Loading...</div>}
              <div
                className={cc([
                  "nds-combobox-item-address",
                  "alignChild--left--center padding--x--s padding--bottom--xs padding--top--s",
                  "title-row",
                ])}
              >
                {" "}
                <span>suggestions</span>
                <span className="narmi-icon-x fontSize--m" />
              </div>
              {suggestions.map((suggestion, i) => {
                return (
                  <div
                    className={cc([
                      "nds-combobox-item-address",
                      "alignChild--left--center padding--x--s padding--y--xs",
                    ])}
                    {...getSuggestionItemProps(suggestion)}
                    key={i}
                  >
                    {suggestion.description}
                  </div>
                );
              })}
              <div
                className={cc([
                  "nds-combobox-item-address",
                  "alignChild--left--center padding--x--s padding--y--xs",
                ])}
              >
                <img src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type AddressUpdate = Record<string, string>;

const AutocompleteStreetAddress = ({
  address_type,
  onUpdate,
  applicantIndex,
  error = "",
  value,
  onChange,
  streetAddressLabel = "Street address",
  autoComplete,
}: {
  address_type?: string;
  // any[] rather than unknown[] so consumers' typed handlers remain assignable
  onUpdate?: (...args: any[]) => void;
  applicantIndex?: number;
  /** Accepted for ContextForm.Field compatibility (injected via cloneElement); unused */
  field?: string;
  error?: string;
  value?: string;
  onChange?: (value: string) => void;
  streetAddressLabel?: string;
  autoComplete?: string;
}) => {
  const POSTAL_CODE_LENGTH = 5;
  const DEFAULT_COUNTRY_CODE = "US";

  // https://developers.google.com/maps/documentation/javascript/reference/places-service#PlaceResult.adr_address
  const geocodeByAdrAddress = (placeId: string) => {
    const ps = new window.google.maps.places.PlacesService(document.createElement("div"));
    const OK = window.google.maps.places.PlacesServiceStatus.OK;

    return new Promise<google.maps.places.PlaceResult | null>((resolve, reject) => {
      ps.getDetails({ placeId }, (result, status) => {
        if (status !== OK) {
          reject(status);
        }
        resolve(result);
      });
    });
  };

  const updateAddress = (value: string | AddressUpdate) => {
    if (typeof value === "string") {
      // PlacesAutocomplete returns a string update
      value = { street_address: value };
    }
    const type = address_type === "Mailing address" ? "mailing" : "primary";
    value["country_code"] = DEFAULT_COUNTRY_CODE; //  API requires two character code
    if (applicantIndex !== undefined) {
      onUpdate?.(applicantIndex, value, type);
    } else {
      onUpdate?.(value, type);
    }
  };

  const parseAddress = (adr_address?: string) => {
    const parse_place = document.createElement("div");
    parse_place.innerHTML = adr_address ?? "";
    const place_fields_map: Record<string, string> = {
      "street-address": "street_address",
      locality: "city",
      region: "region_code",
      "postal-code": "postal_code",
      "country-name": "country_code",
    };
    const place_fields = Object.keys(place_fields_map);
    const address: AddressUpdate = {};
    for (let i = 0; i < place_fields.length; i++) {
      const element = parse_place.querySelector("." + place_fields[i]);
      const key = place_fields_map[place_fields[i]];
      if (element) {
        key === "postal_code"
          ? (address[key] = element.innerHTML.substring(0, POSTAL_CODE_LENGTH))
          : (address[key] = element.innerHTML);
      } else {
        address[key] = "";
      }
    }
    return address;
  };

  const onSelect = (selected: string, placeId: string) => {
    geocodeByAdrAddress(placeId).then((result) => {
      const address = parseAddress(result?.adr_address);
      updateAddress(address);
    });
  };

  return (
    <>
      <PlacesAutocomplete onChange={onChange} value={value} onSelect={onSelect}>
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <AddressInputAndMenu
            streetAddressLabel={streetAddressLabel}
            suggestions={suggestions}
            error={error}
            getInputProps={getInputProps}
            getSuggestionItemProps={getSuggestionItemProps}
            loading={loading}
            onChange={onChange}
            autoComplete={autoComplete}
          />
        )}
      </PlacesAutocomplete>
      <Error error={error} marginTop="none" />
    </>
  );
};

if (PlacesAutocomplete.propTypes) {
  delete PlacesAutocomplete.propTypes.onChange;
  delete PlacesAutocomplete.propTypes.value;
}

export default AutocompleteStreetAddress;
